import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

const CommonInput = React.forwardRef(({
  className,
  label,
  id,
  error,
  required = false,
  leftIcon,
  rightIcon,
  containerClassName = "",
  labelSize = "normal",
  disableAutofill = false,
  disableFocus = false,
  type,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';

  return (
    <div className={`flex flex-col gap-1.5 w-full ${containerClassName}`}>
      {label && (
        <Label
          htmlFor={id}
          className={`font-medium tracking-tight transition-colors duration-200 
            ${labelSize === "small" ? "text-[11px]" : "text-[13px]"}
            ${error ? 'text-destructive' : 'text-foreground/80'}`}
        >
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">
            {leftIcon}
          </div>
        )}
        <Input
          ref={ref}
          id={id}
          type={isPassword ? (showPassword ? 'text' : 'password') : type}
          tabIndex={disableFocus ? -1 : undefined}
          className={`
            flex w-full rounded-lg border border-input bg-background text-sm 
            placeholder:text-muted-foreground/50 transition-all duration-200
            ${leftIcon ? "!pl-10" : "px-3.5"} 
            ${(rightIcon || isPassword) ? "!pr-10" : "px-3.5"}
            ${error ? 'border-destructive focus-visible:ring-destructive/30' : 'border-input focus-visible:ring-primary/30'} 
            ${className || ''} 
          `}
          style={{ height: '40px', borderRadius: '8px' }}
          autoComplete={disableAutofill ? 'off' : props.autoComplete}
          {...props}
        />
        {isPassword ? (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none transition-colors z-10"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        ) : rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">
            {rightIcon}
          </div>
        )}
      </div>
      {error && (
        <p className="text-[11px] text-destructive mt-0.5 animate-in fade-in slide-in-from-top-1 transition-all duration-200">
          {error}
        </p>
      )}
    </div>
  );
});

CommonInput.displayName = 'CommonInput';

export { CommonInput };